// Removed Material Design Icon imports
import { Avatar, Typography } from "antd";
import { useMemo } from "react";
// Added react-icons imports
import { getSocialPlatformSettings } from "@/fixtures/socialPlatforms";
import {
  FiBookmark,
  FiHeart,
  FiMessageSquare,
  FiMoreHorizontal, // Replaced MoreHoriz
  FiSend,
  FiUser, // Replaced PersonOutline
} from "react-icons/fi";
import CaptionExpand from "./components/CaptionExpand"; // Import CaptionExpand
import MediaSection from "./components/MediaSection";
import { BaseSocialCardProps } from "./types";

export interface InstagramCardProps extends BaseSocialCardProps {
  location?: string;
  platformId: string;
}

const InstagramCard = ({
  caption,
  mediaUrls = [],
  username = "User",
  profileUrl,
  postedAt,
  platformId,
  isTemplateVideo,
}: InstagramCardProps) => {
  // const [expanded, setExpanded] = useState(false); // Remove unused state

  // Fetch platform settings and caption limit
  const captionLimit = useMemo(() => {
    const settings = getSocialPlatformSettings(platformId);
    return settings?.limits?.caption;
  }, [platformId]);

  // Truncate caption if limit is provided and caption exceeds it
  const displayCaption = useMemo(() => {
    if (captionLimit && caption.length > captionLimit) {
      return caption.substring(0, captionLimit) + "...";
    }
    return caption;
  }, [caption, captionLimit]);

  return (
    <div className="w-full bg-antd-colorBgContainer text-antd-colorText border border-antd-colorBorderSecondary rounded-lg overflow-hidden shadow-md instagram-card-preview">
      {/* Restore style block for hashtags, using antd token */}
      <style>{`
        .instagram-card-preview .hashtag {
          color: #0095F6; /* Common Instagram Blue */
          text-decoration: none;
        }
        .instagram-card-preview .hashtag:hover {
          text-decoration: underline;
        }
        /* Links (.caption-link) remain default text color */
      `}</style>
      {/* Header */}
      <div className="px-3 py-2 flex justify-between items-center">
        <div className="flex items-center gap-2">
          {/* Keep Instagram gradient border */}
          <div className="w-9 h-9 rounded-full overflow-hidden bg-gradient-to-tr from-yellow-400 to-pink-600 p-0.5 flex items-center justify-center">
            <Avatar
              size={32}
              src={profileUrl}
              icon={!profileUrl ? <FiUser /> : undefined} // Replaced
              className="border-2 border-antd-colorBgContainer" // Use container bg for border
            />
          </div>
          <div className="flex flex-col">
            <span className="font-semibold text-sm">{username}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-medium text-antd-colorTextSecondary">
              {" "}
              {/* Lighter gray */} • Just now
            </span>
          </div>
        </div>
        <FiMoreHorizontal /> {/* Replaced */}
      </div>

      <div className="px-0 flex min-h-[200px]">
        <MediaSection mediaUrls={mediaUrls} isTemplateVideo={isTemplateVideo} />
      </div>

      <div className="px-3 pt-3 pb-2">
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-3">
            {/* Use antd text color for icons */}
            <div className="text-antd-colorText text-xl">
              <FiHeart /> {/* Replaced */}
            </div>
            <div className="text-antd-colorText text-xl">
              <FiMessageSquare /> {/* Replaced */}
            </div>
            <div className="text-antd-colorText text-xl">
              <FiSend /> {/* Replaced */}
            </div>
          </div>
          {/* Use antd text color for icons */}
          <div className="text-antd-colorText text-xl">
            <FiBookmark /> {/* Replaced */}
          </div>
        </div>
        {/* Caption */}
        {displayCaption && (
          <div className="text-sm text-antd-colorText">
            <span className="font-semibold mr-1 inline">{username}</span>
            <CaptionExpand
              text={displayCaption}
              initialLines={1} // Instagram often shows fewer lines initially
              showMoreText="...more"
              linkColor="gray"
              className="inline" // Display inline with username
            />
          </div>
        )}
        <p className="text-antd-colorTextSecondary text-xs mt-2 mb-1">
          Just now
        </p>
      </div>

      {/* Timestamp */}
      {postedAt && (
        <div className="px-4 pb-2">
          <Typography.Text type="secondary" style={{ fontSize: "0.75rem" }}>
            {postedAt}
          </Typography.Text>
        </div>
      )}
    </div>
  );
};

export default InstagramCard;
